import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { Cart } from '@/types/cart'

const CREATE_CART_ITEM_MUTATION = gql`
  mutation createCartItem($input: CartItemCreateInput!) {
    createCartItem(input: $input) {
      cart {
        id
        cartToken
        orderSummary {
          id
          legs {
            id
            legIndex
            legName
            pickupFeeCents
            travelers {
              id
              name
              email
              phone
              shipments {
                id
                trackingId
                totalPriceCents
                retailShipRate
                paidInsuranceCents
                handlingFeeCents
                fuelSurchargeCents
                clearanceFeeCents
                upgradeDiscount
                shipmentLegName
                wholesaleDiscountCents
                shipDate
                arrivalDate
                products {
                  id
                  fullName
                  displayName
                  priceCents
                  type
                  coverageAmountCents
                  productLine {
                    id
                    name
                    icon
                    displayName
                  }
                }
              }
            }
            origin {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
            destination {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
          }
        }
      }
    }
  }
`

// TravelerInputType interface
export interface TravelerInputType {
  // Define properties based on expected structure
  id?: string
  name?: string
  email?: string
  phone?: string
}

// FeeInputType interface
export interface FeeInputType {
  feeId?: string
  feeCategory: string
  amount?: number
  description?: string
}

// CartItemInputType interface
export interface CartItemInputType {
  id?: string
  traveler?: TravelerInputType
  productLineName?: string
  productLineId: string
  productName?: string
  productId?: string
  insurancePrice?: number
  insuranceId?: string
  serviceRateId?: string
  discountSku?: string
  discountId?: string
  packagingName?: string
  packagingId?: string
  additionalInfo?: string
  fees?: FeeInputType[]
  labelId?: string
  dropOff?: boolean
}

export enum DirectionType {
  Inbound = 'inbound',
  Outbound = 'outbound',
}

export interface ShipPointInputType {
  address1?: string
  address2?: string
  city?: string
  state?: string
  postalCode?: string // Mapped from `zip5`
  countryCode?: string
  companyName?: string
  attentionName?: string
  cruise?: boolean
  facilityId?: string
  phoneNumber?: string
}

export interface ShipRouteInputType {
  origin: ShipPointInputType
  destination: ShipPointInputType
}

export interface CartItemCreateInputType {
  cartToken: string
  shipRoute: ShipRouteInputType
  shipDate: string // ISO8601Date is typically represented as a string in TypeScript
  arrivalDate: string
  dropOff: boolean
  direction: DirectionType
  items: CartItemInputType[]
}

interface CreateCartItemMutation {
  createCartItem: Cart
}

export const useCartItemCreateApi = (): UseMutationResult<
  Cart,
  Error,
  CartItemCreateInputType,
  unknown
> => {
  const client = useGraphQLClient() // Avoid re-creating the client on every mutation

  const createCartItemApi = async (input: CartItemCreateInputType): Promise<Cart> => {
    const result = await client.request<CreateCartItemMutation>(CREATE_CART_ITEM_MUTATION, {
      input,
    })
    if (!result?.createCartItem) {
      throw new Error('No cart item returned from API')
    }

    return result.createCartItem
  }

  return useMutation<Cart, Error, CartItemCreateInputType>({
    mutationFn: createCartItemApi,
  })
}

export default useCartItemCreateApi
